/**
 * <Legal /> — the one renderer for all four legal documents.
 *
 * It takes a document from src/content/legal and draws it. There is no
 * layout code in the content files and no legal text in this file, so an
 * edit to a contract never touches a component, and a change of design
 * never touches a contract.
 *
 * WHAT IT GIVES EVERY CLAUSE A LINK
 *   Each section heading carries its `id`, so /terms#section-12 opens at
 *   that section. A paragraph that starts with a clause number carries
 *   one too — /terms#clause-12-3 — which is how a support reply or a
 *   lawyer's email can point at one sentence instead of a whole page.
 *
 * PRINTING
 *   The Print button calls the browser's own print dialog. The reader's
 *   saved PDF is made from the page they are reading, so it can never be
 *   an older version of the text. <PrintStyles /> hides the navigation,
 *   the footer and the button itself, and shows the logo instead.
 *
 * See docs/aeo/AEO-LEDGER.md, task 9.
 */

import PropTypes from 'prop-types';
import styled, { createGlobalStyle } from 'styled-components';

import Page from '../Page';
import Footer from '../Shared/NewFooter';
import Header from '../Shared/NewHeader';

import { colors, ContentWrapper, LogoIcon, mobile, PageWrapper, tablet } from '../styles';

const BULLET = /^[••]\s*/;
const CLAUSE = /^(\d+)\.(\d+)[.\s]/;

/**
 * Print rules for a legal page only.
 *
 * `main` is a scrolling column on screen — ContentWrapper in
 * src/components/styles.js gives it a fixed height — and a printer cannot
 * scroll. Without the two `auto` rules below, a printed copy stops at the
 * bottom of the first screen.
 */
const PrintStyles = createGlobalStyle`
  @media print {
    @page {
      margin: 18mm 16mm;
    }

    body {
      background: white;
    }

    header,
    footer,
    [data-print-hide] {
      display: none !important;
    }

    [data-print-only] {
      display: block !important;
    }

    h1,
    h2 {
      break-after: avoid-page;
      page-break-after: avoid;
    }

    p,
    li {
      break-inside: avoid-page;
      page-break-inside: avoid;
      orphans: 3;
      widows: 3;
    }

    a {
      color: inherit;
      text-decoration: none;
    }
  }
`;

/**
 * The scrolling column, unfrozen for paper.
 *
 * ContentWrapper is a fixed-height box with a scrolling <main> inside it.
 * A printer cannot scroll, so on paper both are released and the document
 * flows down as many pages as it needs.
 */
const Wrapper = styled(ContentWrapper)`
  // One measure for the whole document. The <h1> comes from <Page>, above
  // the article, so the column is set on <main> — otherwise the heading
  // starts at the edge of the window and the text starts 300px in.
  & > main {
    max-width: 908px;
    margin: 0 auto;
    padding: 40px 44px 60px;

    @media (max-width: ${tablet}) {
      padding: 32px 24px 48px;
    }

    @media (max-width: ${mobile}) {
      padding: 24px 15px 40px;
    }
  }

  @media print {
    height: auto;
    overflow: visible;
    padding: 0;

    & > main {
      height: auto;
      min-height: 0;
      overflow: visible;
    }
  }
`;

const Article = styled.div`
  color: ${colors.defaultText};
  font-size: 16px;
  line-height: 1.65;

  @media (max-width: ${mobile}) {
    font-size: 15px;
  }

  h2 {
    font-size: 20px;
    font-weight: 600;
    margin: 36px 0 12px;
    scroll-margin-top: 130px;
  }

  p {
    margin: 0 0 14px;
    scroll-margin-top: 130px;
  }

  ul {
    margin: 0 0 14px;
    padding-left: 22px;
  }

  li {
    margin-bottom: 8px;
  }
`;

const PrintLogo = styled.div`
  display: none;
  margin-bottom: 18px;

  svg {
    height: 40px;
    width: auto;
    fill: ${colors.defaultText};
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 28px;
`;

const LastUpdated = styled.p`
  color: ${colors.fadedText};
  font-size: 14px;
  margin: 0 !important;
`;

const PrintButton = styled.button`
  background: none;
  border: 1px solid ${colors.defaultBorder};
  border-radius: 4px;
  color: ${colors.defaultText};
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  padding: 7px 14px;

  &:hover {
    border-color: ${colors.primaryBorder};
    color: ${colors.primaryButton};
  }
`;

/** 9 April 2021 reads the same everywhere; 04/09/2021 does not. */
export const formatDate = (iso) => {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/** `4.1 Something.` becomes `clause-4-1`, so one clause can be linked to. */
export const clauseId = (paragraph) => {
  const match = CLAUSE.exec(paragraph);
  return match ? `clause-${match[1]}-${match[2]}` : undefined;
};

/**
 * Consecutive bullet paragraphs become one list; everything else stays a
 * paragraph. Grouping happens here so the content files hold nothing but
 * sentences.
 */
export const groupBody = (body = []) =>
  body.reduce((groups, paragraph) => {
    const isBullet = BULLET.test(paragraph);
    const last = groups[groups.length - 1];
    if (isBullet && last && last.type === 'list') {
      last.items.push(paragraph.replace(BULLET, ''));
      return groups;
    }
    groups.push(isBullet ? { type: 'list', items: [paragraph.replace(BULLET, '')] } : { type: 'paragraph', text: paragraph });
    return groups;
  }, []);

const Legal = ({ doc }) => (
  <PageWrapper>
    <PrintStyles />
    <Header omitSearch />
    <Wrapper>
      <Page title={doc.title}>
        <Article>
          {/* Print only: the site header is hidden on paper, so the mark goes here. */}
          <PrintLogo data-print-only aria-hidden="true">
            <LogoIcon />
          </PrintLogo>

          <TopBar>
            <LastUpdated>Last updated: {formatDate(doc.lastUpdated)}</LastUpdated>
            <PrintButton type="button" data-print-hide onClick={() => window.print()}>
              Print / Save as PDF
            </PrintButton>
          </TopBar>

          {doc.sections.map((section) => (
            <section key={section.id}>
              {section.heading ? <h2 id={section.id}>{section.heading}</h2> : null}
              {groupBody(section.body).map((group, index) =>
                group.type === 'list' ? (
                  // eslint-disable-next-line react/no-array-index-key
                  <ul key={`${section.id}-${index}`}>
                    {group.items.map((item) => (
                      <li key={item.slice(0, 80)}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  // eslint-disable-next-line react/no-array-index-key
                  <p key={`${section.id}-${index}`} id={clauseId(group.text)}>
                    {group.text}
                  </p>
                )
              )}
            </section>
          ))}
        </Article>
      </Page>
    </Wrapper>
    <Footer />
  </PageWrapper>
);

Legal.propTypes = {
  doc: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    lastUpdated: PropTypes.string.isRequired,
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        heading: PropTypes.string,
        body: PropTypes.arrayOf(PropTypes.string)
      })
    ).isRequired
  }).isRequired
};

export default Legal;
